import { useCallback, useEffect, useRef, useState } from 'react';
import { getDefaultStore, useAtomValue } from 'jotai';
import { authUserMetaAtom } from '@/store/authAtoms';
import { patientProfileAtom } from '@/store/patientProfileAtom';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { authService, type SessionTimingResponse } from '@/services/authService';

const DEFAULT_IDLE_MIN = 15;   // minutes
const DEFAULT_ABSOLUTE_H = 12; // hours
const COUNTDOWN_START_S = 60;
const WARNING_LEAD_MS = COUNTDOWN_START_S * 1000;
const ACTIVITY_SYNC_INTERVAL_MS = 30_000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll',
];

const store = getDefaultStore();

function readPositiveNumber(raw: number | undefined, fallback: number): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function readDeadline(raw: string | undefined, fallbackMs: number): number {
  const parsed = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : Date.now() + fallbackMs;
}

function secondsUntil(deadlineMs: number): number {
  return Math.max(1, Math.min(COUNTDOWN_START_S, Math.ceil((deadlineMs - Date.now()) / 1000)));
}

function warningDelayMs(deadlineMs: number): number {
  return Math.max(0, deadlineMs - Date.now() - WARNING_LEAD_MS);
}

export type ScreenSessionPhase = 'active' | 'idle-warning' | 'absolute-warning';

export interface ScreenSessionState {
  phase: ScreenSessionPhase;
  countdown: number;
  extend: () => void;
  logout: () => void;
}

export function useScreenSession(): ScreenSessionState {
  const isRemoteAccess = useAtomValue(remoteAccessContextAtom) !== null;

  const [phase, setPhase] = useState<ScreenSessionPhase>('active');
  const [countdown, setCountdown] = useState(0);

  // Mirror phase in a ref so timer callbacks can read it without stale closures.
  const phaseRef = useRef<ScreenSessionPhase>('active');

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loggingOutRef = useRef(false);
  const activitySyncInFlightRef = useRef(false);
  const extendingRef = useRef(false);
  const lastActivitySyncRef = useRef(0);
  const idleMsRef = useRef(DEFAULT_IDLE_MIN * 60_000);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const clearIdle = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearAbsolute = useCallback(() => {
    if (absoluteTimerRef.current) {
      clearTimeout(absoluteTimerRef.current);
      absoluteTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    clearCountdown();
    clearIdle();
    clearAbsolute();

    void authService.logout()
      .catch(() => undefined)
      .finally(() => {
        store.set(authUserMetaAtom, null);
        store.set(patientProfileAtom, null);
        window.location.href = '/home?reason=session_expired';
      });
  }, [clearCountdown, clearIdle, clearAbsolute]);

  // Keep the interval callback pointed at the latest logout without causing
  // the interval effect to re-run.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const startCountdown = useCallback(
    (nextPhase: 'idle-warning' | 'absolute-warning', deadlineMs: number) => {
      clearCountdown();
      phaseRef.current = nextPhase;
      setPhase(nextPhase);

      const tick = () => {
        const remainingMs = deadlineMs - Date.now();
        if (remainingMs <= 0) {
          clearCountdown();
          setCountdown(0);
          logoutRef.current();
          return;
        }
        setCountdown(secondsUntil(deadlineMs));
      };

      tick();
      countdownIntervalRef.current = setInterval(tick, 1000);
    },
    [clearCountdown],
  );

  const scheduleIdleDeadline = useCallback((deadlineMs: number) => {
    clearIdle();
    idleTimerRef.current = setTimeout(() => {
      // Absolute warning takes priority -- don't start idle warning on top of it.
      if (phaseRef.current === 'active') {
        startCountdown('idle-warning', deadlineMs);
      }
    }, warningDelayMs(deadlineMs));
  }, [clearIdle, startCountdown]);

  const scheduleAbsoluteDeadline = useCallback((deadlineMs: number) => {
    clearAbsolute();
    absoluteTimerRef.current = setTimeout(() => {
      clearIdle();
      startCountdown('absolute-warning', deadlineMs);
    }, warningDelayMs(deadlineMs));
  }, [clearAbsolute, clearIdle, startCountdown]);

  const applyServerTiming = useCallback((timing: SessionTimingResponse) => {
    const idleMs = readPositiveNumber(timing.idleTimeoutMinutes, DEFAULT_IDLE_MIN) * 60_000;
    const idleDeadlineMs = readDeadline(timing.idleExpiresAt, idleMs);
    const absoluteDeadlineMs = readDeadline(
      timing.absoluteExpiresAt,
      DEFAULT_ABSOLUTE_H * 3_600_000,
    );

    idleMsRef.current = idleMs;
    scheduleIdleDeadline(idleDeadlineMs);
    scheduleAbsoluteDeadline(absoluteDeadlineMs);
  }, [scheduleAbsoluteDeadline, scheduleIdleDeadline]);

  const syncActivity = useCallback(async () => {
    if (activitySyncInFlightRef.current) return;
    activitySyncInFlightRef.current = true;
    lastActivitySyncRef.current = Date.now();
    try {
      applyServerTiming(await authService.recordActivity());
    } finally {
      activitySyncInFlightRef.current = false;
    }
  }, [applyServerTiming]);

  const extend = useCallback(async () => {
    if (phaseRef.current !== 'idle-warning' || extendingRef.current) return;
    extendingRef.current = true;
    clearCountdown();
    try {
      const timing = await authService.recordActivity();
      phaseRef.current = 'active';
      setPhase('active');
      setCountdown(0);
      lastActivitySyncRef.current = Date.now();
      applyServerTiming(timing);
    } catch {
      // Fail closed if the BFF cannot confirm the session is still valid.
      logoutRef.current();
    } finally {
      extendingRef.current = false;
    }
  }, [applyServerTiming, clearCountdown]);

  const resetIdle = useCallback(() => {
    if (phaseRef.current !== 'active') return;
    scheduleIdleDeadline(Date.now() + idleMsRef.current);
    if (Date.now() - lastActivitySyncRef.current >= ACTIVITY_SYNC_INTERVAL_MS) {
      void syncActivity().catch(() => undefined);
    }
  }, [scheduleIdleDeadline, syncActivity]);

  // Load server-authoritative deadlines, then keep the idle clock synchronized
  // with deliberate browser activity. The absolute deadline always comes from
  // the BFF session creation time, so reloads cannot reset it.
  // Skipped for remote access — the admin's session lifetime is governed by the admin portal.
  useEffect(() => {
    if (isRemoteAccess) return;
    void syncActivity().catch(() => logoutRef.current());
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, resetIdle, { passive: true }));
    return () => {
      clearIdle();
      clearAbsolute();
      clearCountdown();
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, resetIdle));
    };
  }, [isRemoteAccess, syncActivity, resetIdle, clearIdle, clearAbsolute, clearCountdown]);

  return { phase, countdown, extend, logout };
}
